import { useKitbagAuth } from '@statsbomb/kitbag-auth';
import { useAtomValue } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { identCookieAtom } from '@/atoms/cookies';
import { fetchClientAtom } from '@/atoms/queries/client';
import env from '@/env';
import { useSecureBucketAuth } from '@/hooks/useSecureBucketAuth';
import { getFetchClient } from '@/query/client';
import { NoTextLoader } from '../contentState/NoTextLoader';

export const FetchProvider = ({ children }: { children: ReactNode }) => {
  const { getAccessTokenSilently } = useKitbagAuth();
  // Hydrate synchronously on first render instead of via useEffect. Setting it in an
  // effect leaves a window, before that effect runs, where any query atom read above or
  // alongside this provider sees fetchClientAtom's rejecting placeholder, fails, and pays
  // TanStack Query's default ~1s retry backoff before it ever issues a real fetch.
  useHydrateAtoms([[fetchClientAtom, { fetch: getFetchClient(getAccessTokenSilently) }]]);
  const [token, setToken] = useState('');
  // Latches once we let the app render, so the gate can never re-arm on a later render (see below).
  const [gateReleased, setGateReleased] = useState(false);
  // Set if the token fetch fails, so we release the gate instead of hanging on the spinner forever.
  const [tokenFailed, setTokenFailed] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  useSecureBucketAuth(token);

  const searchParams = window.location.search;
  const isHudlLoginAttempted = searchParams.includes('success=');
  const isDodgyEncoding = searchParams.includes('&amp;');
  const identCookieValue = useAtomValue(identCookieAtom);

  const hudlLoginEnabled = !!Number(env.VITE_ENABLE_HUDL_LOGIN);
  // The Hudl ident-cookie handshake is still outstanding: login is on, the cookie isn't set yet,
  // and we haven't come back from configure_cookie (success= in the URL). Shared by the gate and
  // the form-submit guard so the two predicates can't drift.
  const isHandshakeOutstanding = hudlLoginEnabled && !identCookieValue && !isHudlLoginAttempted;

  // Gate until configure_cookie sets the ident cookie: the bootstrap queries need it alongside the
  // bearer token or they 403, blanking the screen (EPT-36853). Use a plain loader, not the app-shell
  // fallback — the shell chrome fires its own authenticated queries that would 403 here too.
  const isAwaitingIdentCookie = isHandshakeOutstanding && !gateReleased && !tokenFailed;

  const navigate = useNavigate();

  useEffect(() => {
    getAccessTokenSilently()
      .then(token => setToken(token))
      .catch(() => setTokenFailed(true));
  }, [getAccessTokenSilently]);

  // Once the app is allowed through, latch it so the gate stays open for the rest of the session.
  useEffect(() => {
    if (!isAwaitingIdentCookie) setGateReleased(true);
  }, [isAwaitingIdentCookie]);

  useEffect(() => {
    if (isHandshakeOutstanding && !gateReleased && token && formRef?.current) formRef.current.requestSubmit();
  }, [isHandshakeOutstanding, gateReleased, token, formRef]);

  // This shouldn't really be necessary, but the redirect we get from calling configure_cookie
  // has the search params encoded as &amp; instead of &, which causes issues with react-router.
  // This effect will replace &amp; with & in the URL if it detects it.
  useEffect(() => {
    if (isDodgyEncoding) {
      const { pathname, hash } = window.location;
      const newSearch = window.location.search.replace(/&amp;/g, '&');
      navigate(pathname + newSearch + hash, { replace: true });
    }
  }, [isDodgyEncoding]);

  return (
    <>
      {hudlLoginEnabled && (
        <form ref={formRef} method="post" className="hidden" action={`${env.VITE_HUDL_URL}/app/users/configure_cookie`}>
          <input name="Token" type="hidden" value={token} />
          <input name="Forward" type="hidden" value={window.location.href} />
        </form>
      )}
      {isAwaitingIdentCookie ? <NoTextLoader /> : children}
    </>
  );
};
